import L from 'leaflet'
import { useMemo } from 'react'
import { GeoJSON } from 'react-leaflet'

import { FieldInfo } from '@/pages/maps/contexts/region-context'

import { isValidField } from '../../../utils/field-validation'
import { createTooltipHTML } from '../../components/tooltip-html'
import {
  Field_HOVER_STYLE,
  getFieldStyles,
} from '../../config/field-styles-config'
import { useStyles } from './styles'

interface FieldsPerimetersProps {
  features: GeoJSON.Feature[]
  regionFieldsInfo: (FieldInfo & { checked: boolean })[]
  onToggleFieldSelection: (fieldId: string) => void
  visibility: boolean
}

export default function FieldsPerimeters({
  features,
  regionFieldsInfo,
  onToggleFieldSelection,
  visibility,
}: FieldsPerimetersProps) {
  const classes = useStyles()

  const geoJsonFeatures = useMemo(() => {
    const fieldsMap = new Map(
      regionFieldsInfo.map((field) => [field.id, field]),
    )

    return features
      .map((feature) => {
        const fieldId = feature.properties?.id
        const fieldInfo = fieldsMap.get(fieldId)

        if (!fieldId || !fieldInfo) return null

        const isSelected = fieldInfo.checked
        const enabled = isValidField(fieldInfo)
        const fieldStyle = getFieldStyles(enabled, isSelected)

        const perimeterStyle = {
          ...fieldStyle,
          opacity: visibility ? fieldStyle.opacity : 0,
          fillOpacity: visibility ? fieldStyle.fillOpacity : 0,
        }

        return (
          <GeoJSON
            key={`${fieldId}-${isSelected}-${enabled}-${fieldInfo.diagnoses_count}-${visibility}`}
            style={perimeterStyle}
            data={feature as any}
            interactive={visibility}
            onEachFeature={(feature, layer) => {
              const tooltipMessage = createTooltipHTML({
                fieldName: fieldInfo.field_name,
                area: fieldInfo.area,
                diagnosticsCount: fieldInfo.diagnoses_count,
              })

              layer.bindTooltip(tooltipMessage, {
                permanent: false,
                direction: 'right',
                offset: [10, 0],
                className: classes.tooltip,
              })

              if (enabled && visibility) {
                layer.on({
                  click: () => onToggleFieldSelection(fieldId),
                  mouseover: (e: L.LeafletMouseEvent) => {
                    e.target.setStyle(Field_HOVER_STYLE)
                  },
                  mouseout: (e: L.LeafletMouseEvent) => {
                    e.target.setStyle(perimeterStyle)
                  },
                })
              }

              return layer
            }}
          />
        )
      })
      .filter(Boolean)
  }, [features, regionFieldsInfo, onToggleFieldSelection, visibility])

  return <>{geoJsonFeatures}</>
}
